import * as api from "@/api";
import ImportantTip from "@/components/important-tip";
import LoadingScreen from "@/components/loading-screen";
import styles from "@/components/orders-page.module.css";
import ProductIcon from "@/components/product-icon";
import * as signals from "@/signals";
import { PaymentMethod, PremiumApp } from "@/types";
import { copyText } from "@/utils";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { JSX } from "react";

function OrdersPage(): JSX.Element {
	const { data, error, isLoading } = useQuery({
		queryFn: () => {
			if (!signals.login.value.username) {
				return null;
			}
			return api.getOrders();
		},
		queryKey: ["orders", signals.login.value.username],
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const methodNameMap = {
		alipay: t("alipay"),
		appstore: t("appStore"),
		paypal: "PayPal",
		wechatpay: t("wechatPay"),
	};

	const productNameMap = {
		airportalPremiumData: t("airportalPremiumData"),
		extraDownloads: t("extraDownloads"),
		webHostingPremium: t("webHostingPremium"),
	};

	const cardElements = data?.map((order) => {
		const amount =
			order.amount.toFixed(2) +
			" " +
			(order.method === PaymentMethod.PAYPAL ? t("usd") : t("cny"));
		const methodName = methodNameMap[order.method] || order.method;
		const productName = productNameMap[order.product] || order.product;

		const timeObject = new Date(order.time);
		const time = timeObject.toLocaleString();
		const isRecentMonth =
			Date.now() - timeObject.getTime() < 30 * 24 * 60 * 60 * 1000;
		const isRefundable =
			isRecentMonth &&
			order.product === "airportalPremiumData" &&
			order.method !== PaymentMethod.APP_STORE;

		const copyOrderNumber = (): void => {
			void copyText(order.orderNumber);
		};

		const requestRefund = (): void => {
			window.open("https://docs.retiehe.com/account/premium-refund.html");
		};

		return (
			<div
				className="card"
				key={order.orderNumber}
			>
				<div className={styles["card-body"]}>
					<div>
						<div className={styles["card-title"]}>
							<ProductIcon
								product={
									{
										airportalPremiumData:
											PremiumApp.AIRPORTAL,
										extraDownloads: PremiumApp.AIRPORTAL,
										webHostingPremium:
											PremiumApp.WEB_HOSTING,
									}[order.product]
								}
								shouldHideOnMobile
								size={20}
							/>
							{productName}
						</div>
						<div className={styles["secondary-info"]}>
							{t("orderNumber") + order.orderNumber}
							<br />
							{t("createdTime") + time}
						</div>
					</div>
					<div>
						{amount}
						<div className={styles["secondary-info"]}>
							{methodName}
						</div>
					</div>
				</div>

				<div className={styles["card-action"]}>
					{isRefundable && (
						<button
							className="action-button"
							onClick={requestRefund}
							type="button"
						>
							{t("requestRefund")}
						</button>
					)}
					<button
						className="action-button"
						onClick={copyOrderNumber}
						type="button"
					>
						{t("copyOrderNumber")}
					</button>
				</div>
			</div>
		);
	});

	return (
		<main>
			<h2>{t("orders")}</h2>
			<div className="description">{t("ordersPageDescription")}</div>
			<div className={styles["cards-container"]}>{cardElements}</div>
			<div className={styles["bottom-tip"]}>
				{t("noMoreItemsToDisplay")}
				<br />
				<FontAwesomeIcon icon={faCircleQuestion} />
				{t("haveQuestionsAboutOrders")}
				<a
					className="theme-color-link"
					href="mailto:support@mail.retiehe.com"
				>
					support@mail.retiehe.com
				</a>
			</div>
			{error && (
				<ImportantTip
					text={error.message}
					title={t("error")}
				/>
			)}
			{isLoading && <LoadingScreen />}
		</main>
	);
}

export default OrdersPage;
